<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9998] bg-black bg-opacity-60 flex items-end sm:items-center justify-center p-0 sm:p-4"
  >
    <div
      class="bg-white w-full sm:max-w-6xl max-h-screen sm:max-h-[95vh] sm:rounded-lg border-0 sm:border border-gray-200 relative animate-fade-in flex flex-col"
    >
      <!-- Modal Loading Overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white bg-opacity-80 z-10 flex items-center justify-center sm:rounded-lg"
      >
        <div class="text-center">
          <div
            class="w-6 h-6 sm:w-8 sm:h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2"
          ></div>
          <p class="text-xs sm:text-sm text-gray-600">
            Loading student details...
          </p>
        </div>
      </div>

      <!-- Fixed Header -->
      <div class="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200 bg-white">
        <!-- Close Button -->
        <button
          @click="$emit('close')"
          :disabled="isLoading"
          class="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation z-10"
          title="Close"
        >
          <X class="w-5 h-5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
        </button>

        <!-- Header -->
        <div class="flex items-center gap-3 sm:gap-4 pr-10 sm:pr-12">
          <!-- Student Photo/Avatar -->
          <div class="flex-shrink-0">
            <div
              v-if="student?.photo"
              class="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 sm:border-4 border-blue-200 z-[999]"
            >
              <img
                :src="student.photo"
                :alt="student.name"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              v-else
              class="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-blue-600 flex items-center justify-center border-2 sm:border-4 border-blue-200"
            >
              <span class="text-sm sm:text-lg md:text-xl font-bold text-white">
                {{ getInitials(student?.name || "") }}
              </span>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <h2
              class="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate"
            >
              {{ student?.name || "Student Details" }}
            </h2>
            <div
              class="flex flex-wrap items-center gap-1 sm:gap-2 mt-1 sm:mt-2"
            >
              <span
                :class="getStatusClass(student?.status)"
                class="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-bold rounded-full border"
              >
                <FileChartLine class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                {{ student?.status }}
              </span>
              <span class="text-xs sm:text-sm text-gray-600">
                Roll No: {{ student?.rollNumber }}
              </span>
              <span class="text-xs sm:text-sm text-gray-600">
                Class:
                {{
                  typeof student?.class === "object"
                    ? student?.class?.name
                    : student?.class
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-4 sm:p-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <!-- Personal Information -->
          <div class="bg-blue-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2"
            >
              <User class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
              <span>Personal Information</span>
            </h3>
            <div class="space-y-3 sm:space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Full Name</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border break-words"
                  >
                    {{ student?.name || "N/A" }}
                  </p>
                </div>

                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Email</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border break-all"
                  >
                    {{ student?.email || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Primary Number</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.phone || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Secondary Number</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.secondaryPhone || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >City</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.city || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >District</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.subDistrict || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >State</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.state || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Pincode</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.pincode || "N/A" }}
                  </p>
                </div>
              </div>
              <div v-if="student?.address">
                <label
                  class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >Detailed Address</label
                >
                <p
                  class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border min-h-[60px] break-words"
                >
                  {{ student?.address || "N/A" }}
                </p>
              </div>
            </div>
          </div>

          <!-- Academic Information -->
          <div class="bg-green-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2"
            >
              <GraduationCap
                class="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0"
              />
              <span>Academic Information</span>
            </h3>
            <div class="space-y-3 sm:space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Roll Number</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.rollNumber || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Class</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{
                      typeof student?.class === "object"
                        ? student?.class?.name
                        : student?.class || "N/A"
                    }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Status</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.status || "N/A" }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Enrollment Date</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{
                      student?.enrollmentDate
                        ? formatDate(student.enrollmentDate)
                        : "N/A"
                    }}
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >DOB</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                  >
                    {{ student?.dob ? formatDate(student.dob) : "N/A" }}
                    <span
                      v-if="student?.dob"
                      class="text-gray-500 ml-2"
                    >
                      ({{ calculateAge(student.dob) }} yrs)
                    </span>
                  </p>
                </div>
                <div>
                  <label
                    class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >Attendance ID</label
                  >
                  <p
                    class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border font-mono break-all"
                  >
                    {{ student?.attendanceID || "N/A" }}
                  </p>
                </div>
                <!-- ✅ Show only if status = passout -->
                <template v-if="student?.status?.toLowerCase() === 'passout'">
                  <div>
                    <label
                      class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Passout Year
                    </label>
                    <p
                      class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                    >
                      {{ student?.passoutYear || "N/A" }}
                    </p>
                  </div>

                  <div>
                    <label
                      class="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Session
                    </label>
                    <p
                      class="text-xs sm:text-sm text-gray-900 bg-white p-2 rounded border"
                    >
                      {{ student?.session || "N/A" }}
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Documents Section -->
          <div class="lg:col-span-2 bg-gray-50 rounded-lg p-3 sm:p-4 md:p-6">
            <h3
              class="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center gap-2"
            >
              <FileText
                class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
              />
              <span>Documents</span>
            </h3>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
            >
              <!-- Aadhaar Card -->
              <div
                class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-2 sm:mb-3">
                  <div
                    class="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0"
                  >
                    <IdCard class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  </div>
                  <h4 class="text-xs sm:text-sm font-semibold text-gray-800">
                    Aadhaar Card
                  </h4>
                </div>
                <div
                  v-if="student?.aadhaarCard"
                  class="space-y-2 sm:space-y-3"
                >
                  <div
                    class="flex items-center gap-2 text-[10px] sm:text-xs text-green-700 bg-green-50 p-2 rounded"
                  >
                    <CheckCircle
                      class="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"
                    />
                    <span>Available</span>
                  </div>
                  <div class="flex flex-col sm:flex-row gap-1 sm:gap-2">
                    <button
                      @click="
                        $emit(
                          'view-document',
                          student.aadhaarCard,
                          'Aadhaar Card',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-blue-600 text-white text-[10px] sm:text-xs rounded hover:bg-blue-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Eye class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      View
                    </button>
                    <button
                      @click="
                        $emit(
                          'download-document',
                          student.aadhaarCard,
                          'aadhaar-card',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-green-600 text-white text-[10px] sm:text-xs rounded hover:bg-green-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Download class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      Download
                    </button>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center py-4 sm:py-6"
                >
                  <div class="text-[10px] sm:text-xs text-gray-500">
                    Not available
                  </div>
                </div>
              </div>

              <!-- Birth Certificate -->
              <div
                class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-2 sm:mb-3">
                  <div
                    class="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0"
                  >
                    <FileText class="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                  </div>
                  <h4 class="text-xs sm:text-sm font-semibold text-gray-800">
                    Birth Certificate
                  </h4>
                </div>
                <div
                  v-if="student?.birthCertificate"
                  class="space-y-2 sm:space-y-3"
                >
                  <div
                    class="flex items-center gap-2 text-[10px] sm:text-xs text-green-700 bg-green-50 p-2 rounded"
                  >
                    <CheckCircle
                      class="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"
                    />
                    <span>Available</span>
                  </div>
                  <div class="flex flex-col sm:flex-row gap-1 sm:gap-2">
                    <button
                      @click="
                        $emit(
                          'view-document',
                          student.birthCertificate,
                          'Birth Certificate',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-blue-600 text-white text-[10px] sm:text-xs rounded hover:bg-blue-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Eye class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      View
                    </button>
                    <button
                      @click="
                        $emit(
                          'download-document',
                          student.birthCertificate,
                          'birth-certificate',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-green-600 text-white text-[10px] sm:text-xs rounded hover:bg-green-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Download class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      Download
                    </button>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center py-4 sm:py-6"
                >
                  <div class="text-[10px] sm:text-xs text-gray-500">
                    Not available
                  </div>
                </div>
              </div>

              <!-- Transfer Certificate -->
              <div
                class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-2 sm:mb-3">
                  <div
                    class="p-1.5 sm:p-2 bg-purple-100 rounded-lg flex-shrink-0"
                  >
                    <FileText class="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                  </div>
                  <h4 class="text-xs sm:text-sm font-semibold text-gray-800">
                    Transfer Certificate
                  </h4>
                </div>
                <div
                  v-if="student?.transferCertificate"
                  class="space-y-2 sm:space-y-3"
                >
                  <div
                    class="flex items-center gap-2 text-[10px] sm:text-xs text-green-700 bg-green-50 p-2 rounded"
                  >
                    <CheckCircle
                      class="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"
                    />
                    <span>Available</span>
                  </div>
                  <div class="flex flex-col sm:flex-row gap-1 sm:gap-2">
                    <button
                      @click="
                        $emit(
                          'view-document',
                          student.transferCertificate,
                          'Transfer Certificate',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-blue-600 text-white text-[10px] sm:text-xs rounded hover:bg-blue-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Eye class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      View
                    </button>
                    <button
                      @click="
                        $emit(
                          'download-document',
                          student.transferCertificate,
                          'transfer-certificate',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-green-600 text-white text-[10px] sm:text-xs rounded hover:bg-green-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Download class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      Download
                    </button>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center py-4 sm:py-6"
                >
                  <div class="text-[10px] sm:text-xs text-gray-500">
                    Not available
                  </div>
                </div>
              </div>

              <!-- Marksheet -->
              <div
                class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm"
              >
                <div class="flex items-center gap-2 mb-2 sm:mb-3">
                  <div class="p-1.5 sm:p-2 bg-red-100 rounded-lg flex-shrink-0">
                    <FileText class="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                  </div>
                  <h4 class="text-xs sm:text-sm font-semibold text-gray-800">
                    Marksheet
                  </h4>
                </div>
                <div
                  v-if="student?.marksheet"
                  class="space-y-2 sm:space-y-3"
                >
                  <div
                    class="flex items-center gap-2 text-[10px] sm:text-xs text-green-700 bg-green-50 p-2 rounded"
                  >
                    <CheckCircle
                      class="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"
                    />
                    <span>Available</span>
                  </div>
                  <div class="flex flex-col sm:flex-row gap-1 sm:gap-2">
                    <button
                      @click="
                        $emit('view-document', student.marksheet, 'Marksheet')
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-blue-600 text-white text-[10px] sm:text-xs rounded hover:bg-blue-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Eye class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      View
                    </button>
                    <button
                      @click="
                        $emit(
                          'download-document',
                          student.marksheet,
                          'marksheet',
                        )
                      "
                      class="flex-1 flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-green-600 text-white text-[10px] sm:text-xs rounded hover:bg-green-700 transition-colors touch-manipulation min-h-[32px]"
                    >
                      <Download class="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      Download
                    </button>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center py-4 sm:py-6"
                >
                  <div class="text-[10px] sm:text-xs text-gray-500">
                    Not available
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Attendance Section -->
          <div class="lg:col-span-2 bg-white rounded-lg p-4 sm:p-6 border">
            <StudentAttendanceAnalytics
              v-if="student?._id"
              :selectedYear="selectedYear"
              :attendanceData="studentAttendanceData"
              @update:selectedYear="handleYearChange"
              @filter-change="handleFilterChange"
            />
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="flex-shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-white">
        <div
          class="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3"
        >
          <button
            @click="$emit('close')"
            class="px-4 py-2.5 sm:py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-200 touch-manipulation min-h-[44px] sm:min-h-[40px]"
          >
            Close
          </button>
          <button
            @click="$emit('edit', student)"
            class="inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 touch-manipulation min-h-[44px] sm:min-h-[40px]"
          >
            <Pencil class="w-3 h-3 sm:w-4 sm:h-4" />
            Edit Student
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch } from "vue";
  import { useStore } from "vuex";
  import {
    X,
    User,
    GraduationCap,
    FileText,
    FileChartLine,
    IdCard,
    CheckCircle,
    Eye,
    Download,
    Pencil,
  } from "lucide-vue-next";
  import StudentAttendanceAnalytics from "@/components/Students/AttendanceGraph.vue";

  /* -----------------------------
   PROPS (FIXED PROPERLY)
----------------------------- */
  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["close", "edit", "view-document", "download-document"]);

  const store = useStore();

  /* -----------------------------
   STATE
----------------------------- */
  const selectedYear = ref(new Date().getFullYear());
  const studentAttendanceData = ref(null);

  /* -----------------------------
   UTILITIES
----------------------------- */
  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "NA";

  const getStatusClass = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-300";
      case "leaved":
        return "bg-red-100 text-red-800 border-red-300";
      case "passedout":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  /* -----------------------------
   LOAD ATTENDANCE WHEN STUDENT CHANGES
----------------------------- */
  watch(
    () => props.student?._id,
    async (newId) => {
      if (!newId) {
        studentAttendanceData.value = null;
        return;
      }

      try {
        const data = await store.dispatch("fetchStudentYearlyAttendance", {
          userId: newId,
          year: selectedYear.value,
        });

        studentAttendanceData.value = data;
      } catch (err) {
        console.error("Failed to load attendance:", err);
        studentAttendanceData.value = null;
      }
    },
    { immediate: true },
  );

  /* -----------------------------
   HANDLE YEAR CHANGE FROM CHILD
----------------------------- */
  const handleYearChange = async (year) => {
    selectedYear.value = year;

    if (!props.student?._id) return;

    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
      });

      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load attendance:", err);
      studentAttendanceData.value = null;
    }
  };
  const handleFilterChange = async ({ year, startDate, endDate }) => {
    if (!props.student?._id) return;

    selectedYear.value = year;

    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
        startDate,
        endDate,
      });

      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load filtered attendance:", err);
      studentAttendanceData.value = null;
    }
  };

  /* -----------------------------
   RESET DATA WHEN MODAL CLOSES
----------------------------- */
  watch(
    () => props.show,
    (visible) => {
      if (!visible) {
        studentAttendanceData.value = null;
      }
    },
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }

  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
